"use client"

import { useState, useCallback } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Slider } from "@/components/ui/slider"
import { Copy, Check, Wand2, Trash2 } from "lucide-react"
import { toast } from "sonner"

const SQL_KEYWORDS = [
  "SELECT", "FROM", "WHERE", "AND", "OR", "NOT", "IN", "LIKE", "BETWEEN",
  "IS", "NULL", "AS", "ON", "JOIN", "INNER", "LEFT", "RIGHT", "OUTER",
  "FULL", "CROSS", "GROUP", "BY", "ORDER", "ASC", "DESC", "HAVING",
  "LIMIT", "OFFSET", "UNION", "ALL", "INTERSECT", "EXCEPT", "INSERT",
  "INTO", "VALUES", "UPDATE", "SET", "DELETE", "CREATE", "TABLE", "INDEX",
  "ALTER", "DROP", "ADD", "COLUMN", "PRIMARY", "KEY", "FOREIGN", "REFERENCES",
  "CONSTRAINT", "UNIQUE", "DEFAULT", "CHECK", "CASCADE", "DISTINCT",
  "COUNT", "SUM", "AVG", "MIN", "MAX", "CASE", "WHEN", "THEN", "ELSE", "END",
  "EXISTS", "ANY", "SOME", "COALESCE", "NULLIF", "CAST", "CONVERT",
  "WITH", "RECURSIVE", "OVER", "PARTITION", "ROW_NUMBER", "RANK", "DENSE_RANK",
  "LEAD", "LAG", "FIRST_VALUE", "LAST_VALUE", "NTH_VALUE",
  "TRUE", "FALSE", "BEGIN", "COMMIT", "ROLLBACK", "TRANSACTION",
  "GRANT", "REVOKE", "TRUNCATE", "EXPLAIN", "ANALYZE", "VACUUM"
]

function formatSQL(sql: string, indentSize: number, uppercase: boolean): string {
  if (!sql.trim()) return ""

  // Remove extra whitespace
  let formatted = sql.replace(/\s+/g, " ").trim()

  // Keywords that start new lines
  const newlineKeywords = [
    "SELECT", "FROM", "WHERE", "AND", "OR", "ORDER BY", "GROUP BY",
    "HAVING", "LIMIT", "OFFSET", "UNION", "INTERSECT", "EXCEPT",
    "INSERT INTO", "VALUES", "UPDATE", "SET", "DELETE FROM",
    "CREATE TABLE", "ALTER TABLE", "DROP TABLE",
    "JOIN", "INNER JOIN", "LEFT JOIN", "RIGHT JOIN", "FULL JOIN",
    "LEFT OUTER JOIN", "RIGHT OUTER JOIN", "FULL OUTER JOIN", "CROSS JOIN",
    "ON", "WITH"
  ]

  // Sort by length (longest first) to match multi-word keywords first
  const sortedKeywords = [...newlineKeywords].sort((a, b) => b.length - a.length)

  // Replace keywords with proper case and newlines
  for (const keyword of sortedKeywords) {
    const regex = new RegExp(`\\b${keyword}\\b`, "gi")
    const replacement = uppercase ? keyword.toUpperCase() : keyword.toLowerCase()

    // Keywords that should be on their own line
    if (["FROM", "WHERE", "ORDER BY", "GROUP BY", "HAVING", "LIMIT", "OFFSET",
         "UNION", "INTERSECT", "EXCEPT", "SET", "VALUES"].includes(keyword.toUpperCase())) {
      formatted = formatted.replace(regex, `\n${replacement}`)
    } else if (keyword.toUpperCase().includes("JOIN")) {
      formatted = formatted.replace(regex, `\n${" ".repeat(indentSize)}${replacement}`)
    } else if (["AND", "OR"].includes(keyword.toUpperCase())) {
      formatted = formatted.replace(regex, `\n${" ".repeat(indentSize * 2)}${replacement}`)
    } else if (keyword.toUpperCase() === "ON") {
      formatted = formatted.replace(regex, ` ${replacement}`)
    } else {
      formatted = formatted.replace(regex, replacement)
    }
  }

  // Format other keywords
  for (const keyword of SQL_KEYWORDS) {
    if (!newlineKeywords.some(nk => nk.toUpperCase().includes(keyword))) {
      const regex = new RegExp(`\\b${keyword}\\b`, "gi")
      formatted = formatted.replace(regex, uppercase ? keyword.toUpperCase() : keyword.toLowerCase())
    }
  }

  // Indent SELECT columns
  formatted = formatted.replace(/\bSELECT\b\s*/gi, (_match) => {
    return (uppercase ? "SELECT" : "select") + "\n" + " ".repeat(indentSize)
  })

  // Handle commas in SELECT
  formatted = formatted.replace(/,\s*/g, ",\n" + " ".repeat(indentSize))

  // Clean up multiple newlines
  formatted = formatted.replace(/\n\s*\n/g, "\n")

  // Trim each line
  formatted = formatted
    .split("\n")
    .map(line => line.trim())
    .filter(line => line)
    .map((line, i) => {
      // Re-apply indentation based on keywords
      if (i === 0) return line

      const upper = line.toUpperCase()
      if (upper.startsWith("SELECT") || upper.startsWith("FROM") || upper.startsWith("WHERE") ||
          upper.startsWith("ORDER") || upper.startsWith("GROUP") || upper.startsWith("HAVING") ||
          upper.startsWith("LIMIT") || upper.startsWith("UNION") || upper.startsWith("SET") ||
          upper.startsWith("VALUES")) {
        return line
      }
      if (upper.startsWith("AND") || upper.startsWith("OR")) {
        return " ".repeat(indentSize) + line
      }
      if (upper.includes("JOIN")) {
        return " ".repeat(indentSize) + line
      }
      // Columns and other content
      return " ".repeat(indentSize) + line
    })
    .join("\n")

  return formatted
}

function minifySQL(sql: string): string {
  return sql
    .replace(/--.*$/gm, "") // Remove single-line comments
    .replace(/\/\*[\s\S]*?\*\//g, "") // Remove multi-line comments
    .replace(/\s+/g, " ") // Collapse whitespace
    .replace(/\s*,\s*/g, ",") // Remove spaces around commas
    .replace(/\s*\(\s*/g, "(") // Remove spaces after (
    .replace(/\s*\)\s*/g, ")") // Remove spaces before )
    .replace(/\s*=\s*/g, "=") // Remove spaces around =
    .trim()
}

const sampleSQL = `SELECT u.id, u.name, u.email, COUNT(o.id) as order_count, SUM(o.total) as total_spent
FROM users u
LEFT JOIN orders o ON u.id = o.user_id
WHERE u.status = 'active' AND u.created_at > '2024-01-01'
GROUP BY u.id, u.name, u.email
HAVING COUNT(o.id) > 0
ORDER BY total_spent DESC
LIMIT 10`

export function SqlFormatter() {
  const [input, setInput] = useState("")
  const [output, setOutput] = useState("")
  const [indentSize, setIndentSize] = useState(4)
  const [uppercase, setUppercase] = useState(true)
  const [copied, setCopied] = useState(false)

  const format = useCallback(() => {
    const formatted = formatSQL(input, indentSize, uppercase)
    setOutput(formatted)
    if (formatted) {
      toast.success("SQL formatted")
    }
  }, [input, indentSize, uppercase])

  const minify = useCallback(() => {
    const minified = minifySQL(input)
    setOutput(minified)
    if (minified) {
      toast.success("SQL minified")
    }
  }, [input])

  const loadSample = () => {
    setInput(sampleSQL)
    setOutput("")
  }

  const clear = () => {
    setInput("")
    setOutput("")
  }

  const copyOutput = async () => {
    if (!output) return
    await navigator.clipboard.writeText(output)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    toast.success("Copied to clipboard")
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>SQL Formatter</CardTitle>
          <CardDescription>
            Format and beautify SQL queries for better readability
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Options */}
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>Indent Size: {indentSize}</Label>
              <Slider
                value={[indentSize]}
                onValueChange={([v]) => setIndentSize(v)}
                min={2}
                max={8}
                step={2}
                className="w-32"
              />
            </div>

            <div className="space-y-2">
              <Label>Keywords</Label>
              <Select value={uppercase ? "upper" : "lower"} onValueChange={(v) => setUppercase(v === "upper")}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="upper">UPPERCASE</SelectItem>
                  <SelectItem value="lower">lowercase</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={loadSample}>
                Load Sample
              </Button>
              <Button variant="outline" size="sm" onClick={clear}>
                <Trash2 className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </div>
          </div>

          {/* Input */}
          <div className="space-y-2">
            <Label htmlFor="sql-input">Input SQL</Label>
            <Textarea
              id="sql-input"
              placeholder="Paste your SQL query here..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="font-mono text-sm min-h-[200px]"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button onClick={format} disabled={!input.trim()}>
              <Wand2 className="h-4 w-4 mr-2" />
              Format
            </Button>
            <Button variant="outline" onClick={minify} disabled={!input.trim()}>
              Minify
            </Button>
          </div>

          {/* Output */}
          {output && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Output</Label>
                <Button variant="ghost" size="sm" onClick={copyOutput}>
                  {copied ? (
                    <Check className="h-4 w-4 mr-1 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 mr-1" />
                  )}
                  Copy
                </Button>
              </div>
              <Textarea
                value={output}
                readOnly
                className="font-mono text-sm min-h-[200px] bg-muted"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tips */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Formatting Tips</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>The formatter will:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Put major clauses (SELECT, FROM, WHERE, etc.) on new lines</li>
            <li>Indent JOIN statements and conditions</li>
            <li>Format keywords consistently (uppercase or lowercase)</li>
            <li>Add proper indentation for readability</li>
            <li>Handle subqueries and nested statements</li>
          </ul>
          <p className="mt-4">
            <strong>Tip:</strong> Use the minify option to compress SQL for storage or transmission.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
